import { queryOptions, type DefaultError } from '@tanstack/react-query';
import { type ReactNode, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { validate as isUUID } from 'uuid';

import { type UUID } from '~/types/common';

import {
  type CustomFieldOption,
  fetchCustomFieldOption,
  fetchCustomFieldOptions,
  queryKeysCustomField,
} from '~/data/customField';

import { type AsyncOption, useSelectDataSource } from '@vestigas/ui';

import { AsyncEntitySelect } from '../AsyncEntitySelect';

import { type Option } from './SelectServerDriven';

const getOptionLabel = ({ displayName }: CustomFieldOption) => displayName;

type P = {
  readonly customFieldId: UUID;
  readonly formatOptionLabel?: (option: Option<CustomFieldOption>) => ReactNode;
  readonly isClearable?: boolean;
  readonly isDisabled?: boolean;
  readonly isMultiSelect?: boolean;
  readonly isRemovableValue?: (data: Option<CustomFieldOption>) => boolean;
  readonly onChange: (
    value: Array<{ id: string }> | { id: string },
    data: CustomFieldOption[] | CustomFieldOption,
  ) => void;
  readonly placeholder?: string;
  readonly value: string[];
} & ComponentStyling;

/**
 * MultiSelect component for working with options for custom fields with predefined values (type "enumeration").
 */
export const MultiSelectCustomFieldValues = ({
  className,
  customFieldId,
  formatOptionLabel,
  isClearable = true,
  isDisabled = false,
  isMultiSelect = true,
  isRemovableValue,
  onChange,
  placeholder = 'Werte auswählen',
  style,
  value,
}: P) => {
  const { t } = useTranslation('common');
  const unresolvedLabel = t(($) => $.customFieldSelect.unavailable);
  const filters = useMemo(() => ({ customFieldId }), [customFieldId]);
  const itemQueryOptions = useCallback(
    (customFieldOptionId: string) => {
      if (!isUUID(customFieldOptionId)) {
        return undefined;
      }

      return {
        queryFn: async () =>
          fetchCustomFieldOption(customFieldId, customFieldOptionId),
        queryKey: queryKeysCustomField.getOption(
          customFieldId,
          customFieldOptionId,
        ),
        select: (option: unknown) => option as CustomFieldOption,
      };
    },
    [customFieldId],
  );
  const dataSource = useSelectDataSource<
    Awaited<ReturnType<typeof fetchCustomFieldOptions>>,
    CustomFieldOption,
    number,
    DefaultError,
    typeof filters
  >({
    getItems: ({ items }) => items,
    getOptionLabel,
    getOptionValue: ({ id }) => id,
    itemQueryOptions,
    listQueryKey: [queryKeysCustomField.base(), 'options'],
    listQueryOptions: ({ filters: requestFilters, search }) => {
      const requestedCustomFieldId =
        requestFilters?.customFieldId ?? customFieldId;

      return queryOptions({
        queryFn: async () => {
          const response = await fetchCustomFieldOptions(
            requestedCustomFieldId,
          );
          const normalizedSearch = search.trim().toLocaleLowerCase();
          return normalizedSearch
            ? {
                ...response,
                items: response.items.filter(({ displayName }) =>
                  displayName.toLocaleLowerCase().includes(normalizedSearch),
                ),
              }
            : response;
        },
        queryKey: [
          ...queryKeysCustomField.getOptions(requestedCustomFieldId),
          { search },
        ] as const,
      });
    },
  });
  const handleChange = useCallback(
    (
      newValues: string | null | string[],
      data: readonly CustomFieldOption[] | CustomFieldOption | undefined,
    ) => {
      onChange(
        Array.isArray(newValues)
          ? newValues.map((id) => ({ id }))
          : { id: newValues ?? '' },
        data as CustomFieldOption[] | CustomFieldOption,
      );
    },
    [onChange],
  );
  const resolvedFormatOptionLabel = useMemo(
    () =>
      formatOptionLabel
        ? ({
            data,
            label,
            value: optionValue,
          }: Extract<AsyncOption<CustomFieldOption>, { status: 'resolved' }>) =>
            formatOptionLabel({ data, label, value: optionValue })
        : undefined,
    [formatOptionLabel],
  );
  const removableValue = useMemo(
    () =>
      isRemovableValue
        ? (option: AsyncOption<CustomFieldOption>) =>
            option.status !== 'resolved' ||
            isRemovableValue({
              data: option.data,
              label: option.label,
              value: option.value,
            })
        : undefined,
    [isRemovableValue],
  );

  return (
    <div style={style}>
      {isMultiSelect ? (
        <AsyncEntitySelect<CustomFieldOption, number, typeof filters>
          className={className}
          dataSource={dataSource}
          filters={filters}
          formatOptionLabel={resolvedFormatOptionLabel}
          hasReadPermission
          isClearable={isClearable}
          isDisabled={isDisabled}
          isMulti
          isRemovableValue={removableValue}
          placeholder={placeholder}
          unresolvedLabel={unresolvedLabel}
          value={value}
          onChange={handleChange}
        />
      ) : (
        <AsyncEntitySelect<CustomFieldOption, number, typeof filters>
          className={className}
          dataSource={dataSource}
          filters={filters}
          formatOptionLabel={resolvedFormatOptionLabel}
          hasReadPermission
          isClearable={isClearable}
          isDisabled={isDisabled}
          isRemovableValue={removableValue}
          placeholder={placeholder}
          unresolvedLabel={unresolvedLabel}
          value={value[0] ?? null}
          onChange={handleChange}
        />
      )}
    </div>
  );
};
